import { useState, useEffect } from "react";
import { getPreviewColorHex } from "../utils/colorPreviewHex";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import MaskAndValid from "utils/maskAndValid";

function isColorSizeMatrix(fam) {
  return (
    fam?.matrixMode === "colorSize" &&
    Array.isArray(fam.matrixSizes) &&
    fam.matrixSizes.length > 0 &&
    Array.isArray(fam.matrixColors) &&
    fam.matrixColors.length > 0
  );
}

function netPrice(prod) {
  if (!prod) return Number.POSITIVE_INFINITY;
  const p = Number(prod.price) || 0;
  const d = Number(prod.discount) || 0;
  return p * (1 - d / 100);
}

function sortProductsByPriceAsc(products) {
  return [...(products || [])].sort((a, b) => netPrice(a) - netPrice(b));
}

function minNetPriceForSize(fam, size) {
  const colors = fam.matrixColors || [];
  let min = Number.POSITIVE_INFINITY;
  colors.forEach((c) => {
    const p = fam.productByColorSize?.[`${c}|||${size}`];
    if (p) min = Math.min(min, netPrice(p));
  });
  return min;
}

function minNetPriceForColor(fam, color) {
  const sizes = fam.matrixSizes || [];
  let min = Number.POSITIVE_INFINITY;
  sizes.forEach((s) => {
    const p = fam.productByColorSize?.[`${color}|||${s}`];
    if (p) min = Math.min(min, netPrice(p));
  });
  return min;
}

function sortMatrixSizesByPriceAsc(fam) {
  return [...(fam.matrixSizes || [])].sort(
    (a, b) => minNetPriceForSize(fam, a) - minNetPriceForSize(fam, b),
  );
}

function sortMatrixColorsByPriceAsc(fam) {
  return [...(fam.matrixColors || [])].sort(
    (a, b) => minNetPriceForColor(fam, a) - minNetPriceForColor(fam, b),
  );
}

function minNetPriceForVariantLegacy(fam, variant) {
  let min = Number.POSITIVE_INFINITY;
  (fam.products || []).forEach((p) => {
    if (p.corLabel && p.corLabel !== "Geral" && p.corLabel !== variant) return;
    min = Math.min(min, netPrice(p));
  });
  return min;
}

function sortVariantsByPriceAsc(fam) {
  return [...(fam.variants || [])].sort(
    (a, b) =>
      minNetPriceForVariantLegacy(fam, a) - minNetPriceForVariantLegacy(fam, b),
  );
}

function MatrixSizeColumnHeader({ text, className, style }) {
  const raw = text == null ? "" : String(text);
  if (!raw.includes(";")) {
    return (
      <Typography className={className} style={style}>
        {raw.trim()}
      </Typography>
    );
  }
  const parts = raw
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean);
  return (
    <Typography className={className} style={{ ...style, lineHeight: 1.45 }}>
      {parts.map((part, i) => (
        <span key={i}>
          {i > 0 ? <br /> : null}
          {part}
        </span>
      ))}
    </Typography>
  );
}

function getRepresentativeProductForSizeColumn(fam, size) {
  const colors = fam.matrixColors || [];
  for (let i = 0; i < colors.length; i++) {
    const p = fam.productByColorSize?.[`${colors[i]}|||${size}`];
    if (p) return p;
  }
  return null;
}

function ColorSizeMatrixColumnHeader({ fam, size, theme }) {
  const rep = getRepresentativeProductForSizeColumn(fam, size);
  const hasDiscount = rep && rep.discount > 0;
  const finalPrice = rep ? rep.price * (1 - (rep.discount || 0) / 100) : 0;

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        gap: 6,
        padding: "6px 4px",
        maxWidth: "fit-content",
        margin: "0 auto",
      }}
    >
      <Box className={`card-status-badge status-badge status-${fam.status}`}>
        {fam.status}
      </Box>
      <MatrixSizeColumnHeader
        text={size}
        className="matrix-prod-name"
        style={{
          fontWeight: 600,
          fontSize: 12,
          color: "#374151",
        }}
      />
      {rep && (
        <>
          <Typography className="matrix-prod-ref">Ref: {rep.ref}</Typography>
          {hasDiscount ? (
            <Box className="matrix-price-container">
              <Typography className="matrix-price-old">
                R$ {MaskAndValid.maskDecimal(rep.price, 0)}
              </Typography>
              <Typography className="matrix-price-new">
                R$ {MaskAndValid.maskDecimal(finalPrice, 0)}
              </Typography>
              <Box className="matrix-discount-badge">-{rep.discount}%</Box>
            </Box>
          ) : (
            <Typography
              className="matrix-price"
              style={{ color: theme.primary }}
            >
              R$ {MaskAndValid.maskDecimal(rep.price, 0)}
            </Typography>
          )}
        </>
      )}
      {!rep && (
        <Typography variant="caption" color="textSecondary">
          —
        </Typography>
      )}
    </Box>
  );
}

export default function GridModal({
  open,
  onClose,
  onSave,
  currentBrand,
  activeFamilyKey,
  families,
  tempGridState,
  setTempGridState,
  selectedBulkColors,
  selectedBulkProducts,
  toggleBulkSelection,
  theme,
}) {
  const [bulkQty, setBulkQty] = useState("");
  const [colorDropdownOpen, setColorDropdownOpen] = useState(false);
  const [productDropdownOpen, setProductDropdownOpen] = useState(false);
  const [selectedMatrixCells, setSelectedMatrixCells] = useState(new Set());

  useEffect(() => {
    if (open && activeFamilyKey && families[currentBrand]?.[activeFamilyKey]) {
      const fam = families[currentBrand][activeFamilyKey];
      const allCells = new Set();
      if (isColorSizeMatrix(fam)) {
        fam.matrixColors.forEach((color) => {
          fam.matrixSizes.forEach((size) => {
            const prod = fam.productByColorSize?.[`${color}|||${size}`];
            if (prod) {
              allCells.add(`${activeFamilyKey}_${prod.id}`);
            }
          });
        });
      } else {
        fam.variants.forEach((variant) => {
          fam.products.forEach((prod) => {
            const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
            allCells.add(compoundId);
          });
        });
      }
      setSelectedMatrixCells(allCells);
    }
  }, [open, activeFamilyKey, currentBrand, families]);

  if (!activeFamilyKey) return null;

  const fam = families[currentBrand][activeFamilyKey];

  const productsSorted = sortProductsByPriceAsc(fam.products);
  let matrixSizesSorted = fam.matrixSizes || [];
  let matrixColorsSorted = fam.matrixColors || [];
  let variantsSorted = fam.variants || [];
  if (isColorSizeMatrix(fam)) {
    matrixSizesSorted = sortMatrixSizesByPriceAsc(fam);
    matrixColorsSorted = sortMatrixColorsByPriceAsc(fam);
    variantsSorted = matrixColorsSorted;
  } else {
    variantsSorted = sortVariantsByPriceAsc(fam);
  }

  const toggleMatrixCheckbox = (compoundId) => {
    const newSet = new Set(selectedMatrixCells);
    if (newSet.has(compoundId)) {
      newSet.delete(compoundId);
    } else {
      newSet.add(compoundId);
    }
    setSelectedMatrixCells(newSet);
  };

  const handleApplyBulk = () => {
    const qty = parseInt(bulkQty, 10);
    if (!isNaN(qty) && qty >= 0) {
      const newTempState = { ...tempGridState };

      if (isColorSizeMatrix(fam)) {
        fam.matrixColors.forEach((color) => {
          const colorMatch =
            selectedBulkColors.size === 0 || selectedBulkColors.has(color);
          if (!colorMatch) return;
          fam.matrixSizes.forEach((size) => {
            const prod = fam.productByColorSize?.[`${color}|||${size}`];
            if (!prod) return;
            const productMatch =
              selectedBulkProducts.size === 0 ||
              selectedBulkProducts.has(prod.id);
            const compoundId = `${activeFamilyKey}_${prod.id}`;
            const isCheckboxSelected = selectedMatrixCells.has(compoundId);
            if (productMatch && isCheckboxSelected) {
              const multiple = prod.multiple || 1;
              let adjustedQty = qty;
              if (adjustedQty > 0) {
                const remainder = adjustedQty % multiple;
                if (remainder !== 0) {
                  adjustedQty = adjustedQty + (multiple - remainder);
                }
              }
              newTempState[compoundId] = adjustedQty;
            }
          });
        });
      } else {
        fam.variants.forEach((variant) => {
          fam.products.forEach((prod) => {
            if (
              prod.corLabel &&
              prod.corLabel !== "Geral" &&
              prod.corLabel !== variant
            ) {
              return;
            }
            const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
            const colorMatch =
              selectedBulkColors.size === 0 || selectedBulkColors.has(variant);
            const productMatch =
              selectedBulkProducts.size === 0 ||
              selectedBulkProducts.has(prod.id);
            const isCheckboxSelected = selectedMatrixCells.has(compoundId);

            if (colorMatch && productMatch && isCheckboxSelected) {
              const multiple = prod.multiple || 1;
              let adjustedQty = qty;
              if (adjustedQty > 0) {
                const remainder = adjustedQty % multiple;
                if (remainder !== 0) {
                  adjustedQty = adjustedQty + (multiple - remainder);
                }
              }
              newTempState[compoundId] = adjustedQty;
            }
          });
        });
      }

      setTempGridState(newTempState);
      setBulkQty("");
    }
  };

  const getColorLabel = () => {
    if (selectedBulkColors.size === 0) return "Todas as Cores";
    if (selectedBulkColors.size === 1) {
      const color = Array.from(selectedBulkColors)[0];
      return color.length > 20 ? color.substring(0, 18) + "..." : color;
    }
    return `${selectedBulkColors.size} Selecionados`;
  };

  const getProductLabel = () => {
    if (selectedBulkProducts.size === 0) return "Todos os Produtos";
    if (selectedBulkProducts.size === 1) {
      const prodId = Array.from(selectedBulkProducts)[0];
      const prod = productsSorted.find((p) => p.id === prodId);
      const name = prod ? prod.name : prodId;
      return name.length > 20 ? name.substring(0, 18) + "..." : name;
    }
    return `${selectedBulkProducts.size} Selecionados`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      className="grid-modal-dialog"
      PaperProps={{
        className: "grid-modal-paper",
      }}
    >
      <DialogTitle className="grid-modal-header">
        <Box className="grid-modal-header-left">
          <Box>
            <Typography
              className="grid-modal-title brand-font"
              style={{ color: theme.primary }}
            >
              {fam.productDisplayName || fam.name}
            </Typography>
            <Typography className="grid-modal-subtitle">
              Selecione variantes
            </Typography>
          </Box>
          <Button onClick={onClose} className="close-btn">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Box className="grid-modal-toolbar">
        <Typography className="toolbar-label" style={{ color: theme.primary }}>
          Massa:
        </Typography>
        <Box
          className="bulk-qty-input-wrapper"
          onFocus={(e) => {
            const wrapper = e.currentTarget;
            if (wrapper) {
              wrapper.style.boxShadow = `0 0 0 1px ${theme.primary}`;
              wrapper.style.borderColor = theme.primary;
            }
          }}
          onBlur={(e) => {
            const wrapper = e.currentTarget;
            if (wrapper && !wrapper.contains(document.activeElement)) {
              wrapper.style.boxShadow = "0 1px 2px 0 rgba(0, 0, 0, 0.05)";
              wrapper.style.borderColor = "#d1d5db";
            }
          }}
        >
          <Typography className="bulk-qty-label">Qtd</Typography>
          <TextField
            type="number"
            value={bulkQty}
            onChange={(e) => setBulkQty(e.target.value)}
            placeholder="0"
            className="bulk-qty-input"
            variant="outlined"
            fullWidth
            InputProps={{
              className: "bulk-qty-input-inner",
              notched: false,
            }}
            inputProps={{
              style: { height: "100%", width: "80px" },
            }}
            style={{
              border: "none",
              background: "none",
              boxShadow: "none",
              height: "100%",
              width: "80px",
            }}
          />
        </Box>
        <Box className="toolbar-divider" />
        <Box className="custom-select-wrapper">
          <Box
            className="custom-select-btn"
            onClick={() => setColorDropdownOpen(!colorDropdownOpen)}
            style={{
              borderColor: colorDropdownOpen ? theme.primary : undefined,
              boxShadow: colorDropdownOpen
                ? `0 0 0 1px ${theme.primary}`
                : undefined,
            }}
          >
            <Typography>{getColorLabel()}</Typography>
            <ExpandMoreIcon />
          </Box>
          {colorDropdownOpen && (
            <Box className="custom-select-list">
              {variantsSorted.map((v) => (
                <Box
                  key={v}
                  className={`custom-select-item ${
                    selectedBulkColors.has(v) ? "selected" : ""
                  }`}
                  onClick={() => toggleBulkSelection("color", v)}
                  style={
                    selectedBulkColors.has(v)
                      ? {
                          backgroundColor: "#eff6ff",
                          color: theme.primary,
                        }
                      : undefined
                  }
                >
                  <Checkbox
                    checked={selectedBulkColors.has(v)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                  <Typography>{v}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box className="custom-select-wrapper">
          <Box
            className="custom-select-btn"
            onClick={() => setProductDropdownOpen(!productDropdownOpen)}
            style={{
              borderColor: productDropdownOpen ? theme.primary : undefined,
              boxShadow: productDropdownOpen
                ? `0 0 0 1px ${theme.primary}`
                : undefined,
            }}
          >
            <Typography>{getProductLabel()}</Typography>
            <ExpandMoreIcon />
          </Box>
          {productDropdownOpen && (
            <Box className="custom-select-list">
              {productsSorted.map((p) => (
                <Box
                  key={p.id}
                  className={`custom-select-item ${
                    selectedBulkProducts.has(p.id) ? "selected" : ""
                  }`}
                  onClick={() => toggleBulkSelection("prod", p.id)}
                  style={
                    selectedBulkProducts.has(p.id)
                      ? {
                          backgroundColor: "#eff6ff",
                          color: theme.primary,
                        }
                      : undefined
                  }
                >
                  <Checkbox
                    checked={selectedBulkProducts.has(p.id)}
                    className="b2b-checkbox"
                    style={{ color: theme.primary }}
                  />
                  <Typography>{p.name}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Button
          className="apply-bulk-btn"
          onClick={handleApplyBulk}
          style={{ backgroundColor: theme.primary }}
        >
          Aplicar
        </Button>

        <Box className="toolbar-info">
          <InfoIcon className="info-icon" style={{ color: theme.primary }} />
          <Typography className="info-text">
            Afeta apenas itens{" "}
            <Box
              component="span"
              className="checkmark-icon"
              style={{ backgroundColor: theme.primary }}
            >
              ✓
            </Box>{" "}
            marcados.
          </Typography>
        </Box>
      </Box>

      <DialogContent className="grid-modal-content">
        <Table className="matrix-table">
          {isColorSizeMatrix(fam) ? (
            <>
              <TableHead className="matrix-header">
                <TableRow>
                  <TableCell className="matrix-header-col sticky">
                    Cor / Tamanho
                  </TableCell>
                  {matrixSizesSorted.map((size) => (
                    <TableCell key={size} className="matrix-header-col">
                      <ColorSizeMatrixColumnHeader
                        fam={fam}
                        size={size}
                        theme={theme}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {matrixColorsSorted.map((color) => (
                  <TableRow key={color} className="matrix-row">
                    <TableCell className="matrix-variant-cell sticky">
                      <Box className="variant-cell-content">
                        <Box
                          className="color-dot"
                          style={{ backgroundColor: getPreviewColorHex(color, fam) }}
                        />
                        <Typography className="variant-name">
                          {color}
                        </Typography>
                      </Box>
                    </TableCell>
                    {matrixSizesSorted.map((size) => {
                      const prod =
                        fam.productByColorSize?.[`${color}|||${size}`];
                      if (!prod) {
                        return (
                          <TableCell key={size} className="matrix-input-cell">
                            <Typography variant="caption" color="textPrimary">
                              —
                            </Typography>
                          </TableCell>
                        );
                      }
                      const compoundId = `${activeFamilyKey}_${prod.id}`;
                      const val = tempGridState[compoundId] || "";
                      const multiple = prod.multiple || 1;
                      const multipleText =
                        multiple > 1 ? `Múlt: ${multiple}` : "-";
                      const isChecked = selectedMatrixCells.has(compoundId);

                      return (
                        <TableCell
                          key={`${color}-${size}`}
                          className="matrix-input-cell"
                        >
                          <Box
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 4,
                            }}
                          >
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleMatrixCheckbox(compoundId)}
                              className="matrix-checkbox b2b-checkbox"
                              style={{ color: theme.primary }}
                            />
                            <TextField
                              type="number"
                              value={val}
                              onChange={(e) => {
                                const value = e.target.value;
                                let intVal = parseInt(value, 10);
                                const newTempState = { ...tempGridState };

                                if (intVal > 0) {
                                  const remainder = intVal % multiple;
                                  if (remainder !== 0) {
                                    intVal = intVal + (multiple - remainder);
                                  }
                                  newTempState[compoundId] = intVal;
                                } else {
                                  delete newTempState[compoundId];
                                }

                                setTempGridState(newTempState);
                              }}
                              onFocus={(e) => {
                                e.target.style.outline = `2px solid ${theme.primary}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.outline = "none";
                              }}
                              placeholder={multipleText}
                              className="matrix-input"
                              min="0"
                              step={multiple}
                              InputProps={{
                                className: "matrix-input-inner",
                              }}
                            />
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </>
          ) : (
            <>
              <TableHead className="matrix-header">
                <TableRow>
                  <TableCell className="matrix-header-col sticky">
                    Cor / Variante
                  </TableCell>
                  {productsSorted.map((prod) => {
                    const finalPrice =
                      prod.price * (1 - (prod.discount || 0) / 100);
                    const hasDiscount = prod.discount > 0;

                    return (
                      <TableCell key={prod.id} className="matrix-header-col">
                        <Box
                          className={`card-status-badge status-badge status-${fam.status}`}
                        >
                          {fam.status}
                        </Box>
                        <Typography className="matrix-prod-name">
                          {prod.name}
                        </Typography>
                        <Typography className="matrix-prod-ref">
                          Ref: {prod.ref}
                        </Typography>
                        {hasDiscount ? (
                          <Box className="matrix-price-container">
                            <Typography className="matrix-price-old">
                              R$ {MaskAndValid.maskDecimal(prod.price, 0)}
                            </Typography>
                            <Typography className="matrix-price-new">
                              R$ {MaskAndValid.maskDecimal(finalPrice, 0)}
                            </Typography>
                            <Box className="matrix-discount-badge">
                              -{prod.discount}%
                            </Box>
                          </Box>
                        ) : (
                          <Typography
                            className="matrix-price"
                            style={{ color: theme.primary }}
                          >
                            R$ {MaskAndValid.maskDecimal(prod.price, 0)}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {variantsSorted.map((variant) => (
                  <TableRow key={variant} className="matrix-row">
                    <TableCell className="matrix-variant-cell sticky">
                      <Box className="variant-cell-content">
                        <Box
                          className="color-dot"
                          style={{ backgroundColor: getPreviewColorHex(variant, fam) }}
                        />
                        <Typography className="variant-name">
                          {variant}
                        </Typography>
                      </Box>
                    </TableCell>
                    {productsSorted.map((prod) => {
                      const mismatch =
                        prod.corLabel &&
                        prod.corLabel !== "Geral" &&
                        prod.corLabel !== variant;
                      if (mismatch) {
                        return (
                          <TableCell
                            key={`${prod.id}-${variant}`}
                            className="matrix-input-cell"
                          >
                            <Typography variant="caption" color="textPrimary">
                              —
                            </Typography>
                          </TableCell>
                        );
                      }
                      const compoundId = `${activeFamilyKey}_${prod.id}_${variant}`;
                      const val = tempGridState[compoundId] || "";
                      const multiple = prod.multiple || 1;
                      const multipleText =
                        multiple > 1 ? `Múlt: ${multiple}` : "-";
                      const isChecked = selectedMatrixCells.has(compoundId);

                      return (
                        <TableCell key={prod.id} className="matrix-input-cell">
                          <Box className="matrix-input-wrapper">
                            <Checkbox
                              checked={isChecked}
                              onChange={() => toggleMatrixCheckbox(compoundId)}
                              className="matrix-checkbox b2b-checkbox"
                              style={{ color: theme.primary }}
                            />
                            <TextField
                              type="number"
                              value={val}
                              onChange={(e) => {
                                const value = e.target.value;
                                let intVal = parseInt(value, 10);
                                const newTempState = { ...tempGridState };

                                if (intVal > 0) {
                                  const remainder = intVal % multiple;
                                  if (remainder !== 0) {
                                    intVal = intVal + (multiple - remainder);
                                  }
                                  newTempState[compoundId] = intVal;
                                } else {
                                  delete newTempState[compoundId];
                                }

                                setTempGridState(newTempState);
                              }}
                              onFocus={(e) => {
                                e.target.style.outline = `2px solid ${theme.primary}`;
                              }}
                              onBlur={(e) => {
                                e.target.style.outline = "none";
                              }}
                              placeholder={multipleText}
                              className="matrix-input"
                              min="0"
                              step={multiple}
                              InputProps={{
                                className: "matrix-input-inner",
                              }}
                            />
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </>
          )}
        </Table>
      </DialogContent>

      <DialogActions className="grid-modal-actions">
        <Button onClick={onClose} className="cancel-btn">
          Cancelar
        </Button>
        <Button
          onClick={onSave}
          className="save-btn"
          style={{
            backgroundColor: theme.primary,
          }}
          startIcon={<SaveIcon />}
        >
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
